import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectorRef, ElementRef, TemplateRef } from '@angular/core';
import { Location } from '@angular/common'
import { ActivatedRoute } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { UntypedFormBuilder, FormControl, Validators, ControlContainer, FormGroupDirective, UntypedFormGroup, UntypedFormControl } from '@angular/forms';

import * as FileSaver from 'file-saver';
import { GlobalsService } from '../../services/globals.service';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, startWith } from 'rxjs/operators';

import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';


@Component({
	selector: 'app-company-setup',
	templateUrl: './company-setup.component.html',
	styleUrls: ['./company-setup.component.scss']
})
export class CompanySetupComponent implements OnInit, OnDestroy {
	@ViewChild(MatSort, { static: false }) sort: MatSort;
	@ViewChild(MatPaginator) paginator: MatPaginator;
	/** Structured keys edited elsewhere (e.g. Email tab); omit from bulk config form so Update does not ship raw JSON. */
	private readonly skipGenericConfigKeys = new Set<string>(['vendorInvoiceDiscrepancyNotify']);
	company: any = [];
	companyForm: UntypedFormGroup = this.fb.group({});
	configForm: UntypedFormGroup = this.fb.group({});
	controls: any = [];
	color = 'blue';
	configs: any = [];
	allconfigs: any = [];

	confname = new FormControl('');
	confvalue = new FormControl('');
	conftype = new FormControl('text');
	sectionVisible = false;
	logo: any = '';
	logoFileName: any = '';

	@ViewChild('details') detailsRef: TemplateRef<any>;
	termsModal = new FormControl('');
	termscontrol: any;

	preferencesSearch = new FormControl('');
	configSearch = new FormControl('');
	filteredControls: string[] = [];
	filteredConfigs: string[] = [];
	private destroy$ = new Subject<void>();
	selectedSetupTabIndex = 0;

	constructor(private globalSearchService: GlobalSearchService, private modalService: NgbModal, private location: Location, public cdr: ChangeDetectorRef, private globalsService: GlobalsService, private fb: UntypedFormBuilder, private route: ActivatedRoute) {
		this.color = this.globalSearchService.getColor();
	}

	ngOnInit(): void {
		this.loadData();
		this.preferencesSearch.valueChanges.pipe(
			startWith(''),
			debounceTime(200),
			takeUntil(this.destroy$)
		).subscribe(term => {
			const q = ((term || '').trim()).toLowerCase();
			if (!q) {
				this.filteredControls = this.controls;
			} else {
				this.filteredControls = this.controls.filter(c => {
					const keyMatch = this.properCase(c).toLowerCase().includes(q);
					const val = this.companyForm.get(c)?.value;
					const valueMatch = String(val ?? '').toLowerCase().includes(q);
					return keyMatch || valueMatch;
				});
			}
			this.cdr.markForCheck();
		});
		this.configSearch.valueChanges.pipe(
			startWith(''),
			debounceTime(200),
			takeUntil(this.destroy$)
		).subscribe(term => {
			const q = ((term || '').trim()).toLowerCase();
			if (!q) {
				this.filteredConfigs = this.configs;
			} else {
				this.filteredConfigs = this.configs.filter(c => {
					const meta = this.getConfig(c);
					const keyMatch = meta && (meta.name || '').toLowerCase().includes(q);
					const formVal = this.configForm.get(c)?.value;
					const valueMatch = String(formVal ?? '').toLowerCase().includes(q);
					return keyMatch || valueMatch;
				});
			}
			this.cdr.markForCheck();
		});
		this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((q) => {
			if (q['tab'] === 'mail-intake') {
				this.selectedSetupTabIndex = 3;
				this.cdr.markForCheck();
			}
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.logo = '';
	}

	toggleSection() {
		this.sectionVisible = !this.sectionVisible;
	}

	loadData() {
		this.globalsService.getCompany().pipe(takeUntil(this.destroy$)).subscribe(details => {
			this.company = details;
			if (this.company.logo) {
				this.logo = this.company.logo;
			}
			this.controls = [];
			const controls = [];
			const formGroup: UntypedFormGroup = new UntypedFormGroup({});
			Object.keys(details).forEach(key => {
				const value = (details[key]) ? details[key] : '';
				this.controls.push(key);
				controls.push({ name: key, control: new UntypedFormControl(value, []) });
			});
			controls.forEach(f => {
				formGroup.addControl(f.name, f.control, {});
			});
			this.companyForm = formGroup;
			this.filteredControls = this.controls;
			const q = (this.preferencesSearch.value || '').trim().toLowerCase();
			if (q) {
				this.filteredControls = this.controls.filter(c => {
					const keyMatch = this.properCase(c).toLowerCase().includes(q);
					const val = formGroup.get(c)?.value;
					const valueMatch = String(val ?? '').toLowerCase().includes(q);
					return keyMatch || valueMatch;
				});
			}
			this.cdr.markForCheck();
		});

		this.globalsService.getConfigKeyPairsWithType().pipe(takeUntil(this.destroy$)).subscribe(details => {
			this.configs = [];
			const controls = [];
			this.allconfigs = details;
			const formGroup: UntypedFormGroup = new UntypedFormGroup({});
			Object.keys(details).forEach(key => {
				if (this.skipGenericConfigKeys.has(key)) {
					return;
				}
				const meta = details[key];
				let value = meta ? meta.value : '';
				if (meta && meta.type === 'json' && typeof value === 'string' && value.trim() !== '') {
					try {
						value = JSON.stringify(JSON.parse(value), null, 2);
					} catch {
						/* keep raw string so user can fix */
					}
				}
				this.configs.push(key);
				controls.push({ name: key, control: new UntypedFormControl(value, []) });
			});
			controls.forEach(f => {
				formGroup.addControl(f.name, f.control, {});
			});
			this.configForm = formGroup;
			this.filteredConfigs = this.configs;
			const q = (this.configSearch.value || '').trim().toLowerCase();
			if (q) {
				this.filteredConfigs = this.configs.filter(c => {
					const meta = this.getConfig(c);
					const keyMatch = meta && (meta.name || '').toLowerCase().includes(q);
					const formVal = formGroup.get(c)?.value;
					const valueMatch = String(formVal ?? '').toLowerCase().includes(q);
					return keyMatch || valueMatch;
				});
			}
			this.cdr.markForCheck();
		});
	}


	properCase(str) {
		return str.split(' ').map(function(val) {
			return val.charAt(0).toUpperCase() + val.substr(1).toLowerCase();
		}).join(' ');
	}

	addConfig() {
		const configData = {
			name: this.confname.value,
			value: this.confvalue.value,
			type: this.conftype.value
		};
		this.globalsService.addConfig(configData).pipe(takeUntil(this.destroy$)).subscribe((result) => {
			if (result.success) {
				this.globalSearchService.showNotification('Configuration Added', 'success', 'bottom', 'right');
				this.confname.setValue('');
				this.confvalue.setValue('');
				this.conftype.setValue('text');
				this.sectionVisible = false;
				this.loadData();
			}
		});
	}


	onSelect(event: any): void {
	  const file = event.addedFiles[0];
	  this.logoFileName = file.name; // Store the original file name
	  const reader = new FileReader();

	  reader.onload = (e: any) => {
			this.logo = e.target.result;
	  };

	  reader.readAsDataURL(file);
	}


	getConfig(config: any) {
		return this.allconfigs[config] || { name: config, value: '', type: 'text' };
	}


	back(): void {
		this.location.back()
	}

	update() {
	  const formData = { ...this.companyForm.value };

	  if (this.logo && typeof this.logo === 'string' && this.logo.startsWith('data:') && this.logoFileName) {
			formData.logo = this.logo;
			formData.logoFileName = this.logoFileName;
	  }

	  this.globalsService.updateCompany(formData).pipe(takeUntil(this.destroy$)).subscribe({
			next: (res: any) => {
				if (res && res.error) {
					this.globalSearchService.showNotification(res.error, 'danger', 'bottom', 'right');
				} else {
					this.globalSearchService.showNotification('Updated Company Settings', 'success', 'bottom', 'right');
				}
			},
			error: (err: any) => {
				const msg = err?.error?.error || err?.message || 'Failed to update company';
				this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
			}
		});
	}

	updateConfig() {
		const raw = { ...this.configForm.value };
		const payload: Record<string, unknown> = {};
		for (const key of Object.keys(raw)) {
			const meta = this.getConfig(key);
			if (meta.type === 'json') {
				const s = String(raw[key] ?? '').trim();
				if (s === '') {
					payload[key] = '';
					continue;
				}
				try {
					payload[key] = JSON.stringify(JSON.parse(s));
				} catch {
					this.globalSearchService.showNotification(
						`Invalid JSON for “${meta.name || key}”. Fix or clear the field.`,
						'danger',
						'bottom',
						'right'
					);
					return;
				}
			} else {
				payload[key] = raw[key];
			}
		}
		this.globalsService.updateConfig(payload).pipe(takeUntil(this.destroy$)).subscribe({
			next: () => {
				this.globalSearchService.showNotification('Updated Configuration', 'success', 'bottom', 'right');
				this.loadData();
			},
			error: (err: any) => {
				const msg = err?.error?.message || err?.message || 'Failed to update configuration';
				this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
			},
		});
	}


	openTermsModal(control: any){
		this.cdr.detectChanges()
		this.termsModal.setValue(this.companyForm.get(control).value);
		this.termscontrol = control;
		this.modalService.open(this.detailsRef, { ariaLabelledBy: 'modal-title', size: 'xl' }).result.then((result) => { }, (reason) => {
			this.termscontrol = false;
		});
	}

	dismissTerms(action){
		if(action){
			this.companyForm.get(this.termscontrol).setValue(this.termsModal.getRawValue());
		}
		this.modalService.dismissAll()
	}

	updateForm(control, event){
		control.setValue(event);
	}








}
